<template>
  <section>
    <div class="flex items-baseline justify-between gap-3 mb-2">
      <h4 class="text-xs font-bold uppercase tracking-ds-caps text-muted-foreground">
        Cash flows · {{ rows.length }} {{ rows.length === 1 ? 'entry' : 'entries' }}
      </h4>
    </div>

    <ul class="border border-border rounded-ds-lg overflow-hidden">
      <li
        v-for="(flow, index) in rows"
        :key="`${flow.date}-${index}`"
        class="flex flex-col sm:flex-row sm:items-center gap-4 px-4 py-2.5 bg-card"
        :class="index < rows.length - 1 ? 'border-b border-border' : ''"
      >
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <span class="text-sm font-bold text-foreground truncate">
              {{ investorLabel(flow.investorIndex) }}
            </span>
          </div>
          <p class="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            {{ dateLabel(flow.date) }} ·
            <ArrowDownLeft
              v-if="isContribution(flow.type)"
              class="size-3 shrink-0"
            />
            <ArrowUpRight
              v-else
              class="size-3 shrink-0 text-success"
            />
            {{ CASH_FLOW_TYPE_LABEL[flow.type] ?? flow.type }}
          </p>
        </div>

        <p
          class="text-base font-bold tracking-ds-snug"
          :class="isDistribution(flow.type) ? 'text-success' : 'text-foreground'"
        >
          {{ isContribution(flow.type) ? '−' : '+' }}{{ money(flow.amount) }}
        </p>
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { ArrowDownLeft, ArrowUpRight } from '@lucide/vue'

import { FRACTION_DIGITS } from '@/const'

import { getCurrencySymbol } from '@/helpers/common'
import { stringToLocalDateString } from '@/helpers/dates'
import { numberFormat } from '@/helpers/numbers'

import { CASH_FLOW_TYPE_LABEL } from '../utils/const'
import { isContribution, isDistribution, sortedCashFlows } from '../utils/helpers'
import { WizardCashFlow, WizardInvestor } from '../utils/types'

/*
 * What the cash-flows step logged, read back on the summary — the same list
 * and the same per-row direction the step draws, without the controls: adding
 * and removing an entry belongs to the step that owns them, same reasoning as
 * `SummaryValues`.
 *
 * Nothing at all when nothing was recorded — the caller draws this only for a
 * list with something in it, same as `SummaryValues`'s own rule. The step is
 * optional, and the summary is what the wizard will create, not a list of
 * what it will not.
 */
const { cashFlows: recorded, investors, currency } = defineProps<{
  /** What the step recorded, in whatever order it was entered. */
  cashFlows: WizardCashFlow[]
  /** For the investor name each row names by position — see WizardCashFlow. */
  investors: WizardInvestor[]
  /** The wizard's currency, for the figures. */
  currency?: string
  /** The cash-flows step's own label, so a rename cannot leave this quoting it. */
  stepLabel: string
}>()

defineEmits<{
  (e: 'edit'): void
}>()

const rows = computed(() => sortedCashFlows(recorded))

const investorLabel = (index: number) => investors[index]?.accountName || `Investor ${index + 1}`

const dateLabel = (date: string) => stringToLocalDateString(date) ?? date

const money = (amount: number) =>
  numberFormat(amount, {
    currency: getCurrencySymbol(currency),
    fractionDigits: 0,
    fractionDigitsMax: FRACTION_DIGITS,
  })
</script>
